import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { TID } from '@/lib/testIds';
import { Lock } from 'lucide-react';
import { Logo } from '@/components/Logo';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAdmin } = useAuth();
  const [email, setEmail] = useState('admin@smithybrowns.co.uk');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => { if (isAdmin) navigate('/admin', { replace: true }); }, [isAdmin, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      if (u.must_change_password) {
        navigate('/admin/change-password', { replace: true });
        return;
      }
      const to = location.state?.from?.pathname || '/admin';
      navigate(to, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={TID.admin.loginPage} className="min-h-screen sb-hero-bg grid place-items-center px-4">
      <div className="max-w-md w-full sb-card sb-gold-glow p-7">
        <div className="flex justify-center"><Logo variant="header" /></div>
        <h1 className="font-display text-3xl text-white mt-5 inline-flex items-center gap-2"><Lock size={20} className="text-[hsl(var(--sb-gold))]" /> SIGN IN</h1>
        <p className="text-sm text-white/65 mt-1">Manage orders, reservations and your menu.</p>
        <form onSubmit={submit} className="mt-6 space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-white/55">Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 w-full h-11 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-focus))]" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-white/55">Password</span>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 w-full h-11 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-focus))]" />
          </label>
          <button type="submit" disabled={submitting} data-testid={TID.admin.loginSubmit} className="w-full h-12 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold hover:bg-[hsl(var(--sb-gold-2))] disabled:opacity-60">
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
          <div className="text-center">
            <a href="/admin/forgot-password" data-testid="admin-forgot-password-link" className="text-sm text-[hsl(var(--sb-gold))] hover:underline">Forgot password?</a>
          </div>
        </form>
        <p className="text-[11px] text-white/45 mt-5 text-center">Default credentials: admin@smithybrowns.co.uk / admin123</p>
      </div>
    </div>
  );
}
